import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  CalendarClock,
  UtensilsCrossed,
  CheckCircle2,
  ClipboardCheck,
  Image as ImageIcon,
  Loader2,
  Music,
  Pencil,
  Plus,
  Trash2,
  Upload,
  Users,
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  createCreneau,
  createParticipant,
  deleteCreneau,
  deleteParticipant,
  envoyerVisuelEvenement,
  fetchEvenementReferentiel,
  fetchEvenementsBureau,
  fetchParticipants,
  fetchPlanningEvenement,
  retirerVisuelEvenement,
  updateCreneau,
  updateParticipant,
  type CreneauPayload,
  type ParticipantPayload,
} from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { nomEdition } from '../utils/evenement';
import GrillePlanning from '../components/GrillePlanning';
import OngletRepas from '../components/OngletRepas';
import OngletConcert from '../components/OngletConcert';

type Onglet = 'visuel' | 'participants' | 'planning' | 'repas' | 'concert';

const participantVide: ParticipantPayload = {
  type: 'benevole',
  statut: 'confirme',
  activitySectorId: null,
  bureauRoleId: null,
  commentaire: '',
};

const creneauVide: CreneauPayload = {
  mission: 'accueil',
  statutConfirmation: 'propose',
  debut: '',
  fin: '',
  commentaire: '',
};

/** Les champs date attendent le format accepté par datetime-local. */
const pourChamp = (iso?: string | null) => (iso ? iso.slice(0, 16) : '');

const couleurStatut: Record<string, string> = {
  confirme: 'bg-green-100 text-green-800',
  propose: 'bg-amber-100 text-amber-800',
  refuse: 'bg-red-100 text-red-800',
  realise: 'bg-blue-100 text-blue-800',
};

/**
 * Écran complet d'un événement : visuel, participants et planning.
 *
 * Ces trois volets n'existaient que dans l'interface Twig, inutilisable au
 * téléphone. Les deux interfaces couvrent désormais les mêmes informations.
 */
export default function BureauEvenementDetailPage() {
  const { id } = useParams<{ id: string }>();
  const evenementId = Number(id);
  const { user, isLoading: authLoading } = useAuth();
  const queryClient = useQueryClient();

  // `?onglet=planning` ouvre directement le bon volet : un lien envoyé depuis
  // Mon espace ou par message doit mener où il annonce, pas au visuel.
  const [params] = useSearchParams();
  const [onglet, setOnglet] = useState<Onglet>(() => {
    const demande = params.get('onglet');

    return demande === 'participants' || demande === 'planning' || demande === 'repas'
      || demande === 'concert'
      ? demande
      : 'visuel';
  });
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const peutGerer = user?.peutGererEvenements === true;

  const evenementsQuery = useQuery({
    queryKey: ['bureau-evenements'],
    queryFn: fetchEvenementsBureau,
    enabled: peutGerer,
  });

  const referentielQuery = useQuery({
    queryKey: ['evenement-referentiel', evenementId],
    queryFn: () => fetchEvenementReferentiel(evenementId),
    enabled: peutGerer && Number.isFinite(evenementId),
  });

  const evenement = (evenementsQuery.data?.evenements ?? []).find(
    (e: any) => e.id === evenementId
  );

  const succes = (message: string, cles: string[]) => {
    setNotice({ type: 'success', message });
    cles.forEach((cle) => queryClient.invalidateQueries({ queryKey: [cle, evenementId] }));
    queryClient.invalidateQueries({ queryKey: ['bureau-evenements'] });
    queryClient.invalidateQueries({ queryKey: ['evenements-publics'] });
  };
  const echec = (error: Error) => setNotice({ type: 'error', message: error.message });

  if (authLoading || evenementsQuery.isPending) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!peutGerer) {
    return (
      <div className="p-4 lg:p-0 pb-24 lg:pb-0">
        <div className="card bg-red-50 border-red-200 text-red-800 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5" />
          <div>
            <p className="font-semibold">Accès réservé</p>
            <p className="text-sm mt-1">
              La gestion des événements est réservée aux administrateurs et au président du bureau.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!evenement) {
    return (
      <div className="p-4 lg:p-0 pb-24 lg:pb-0 space-y-4">
        <p className="text-gray-600 dark:text-gray-400">Cet événement est introuvable.</p>
        <Link to="/bureau/evenements" className="btn-outline inline-flex items-center gap-2 text-sm px-3 py-1.5">
          <ArrowLeft className="w-4 h-4" /> Retour aux événements
        </Link>
      </div>
    );
  }

  const referentiel = referentielQuery.data;

  const onglets = [
    { value: 'visuel' as const, label: 'Visuel', icone: ImageIcon },
    { value: 'participants' as const, label: 'Participants', icone: Users },
    { value: 'planning' as const, label: 'Planning', icone: CalendarClock },
    { value: 'repas' as const, label: 'Repas', icone: UtensilsCrossed },
    { value: 'concert' as const, label: 'Concert', icone: Music },
  ];

  return (
    <div className="p-4 lg:p-0 space-y-5 lg:space-y-6 pb-24 lg:pb-0">
      <div>
        <Link
          to="/bureau/evenements"
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-2 dark:text-gray-400"
        >
          <ArrowLeft className="w-4 h-4" /> Tous les événements
        </Link>
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">{nomEdition(evenement.nom, evenement.dateDebut)}</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {format(new Date(evenement.dateDebut), 'EEEE d MMMM yyyy à HH:mm', { locale: fr })}
          {evenement.lieu ? ` — ${evenement.lieu}` : ''}
        </p>
      </div>

      {notice && (
        <div
          className={`card flex items-start gap-3 text-sm ${
            notice.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}
        >
          {notice.type === 'success' ? (
            <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" />
          ) : (
            <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          )}
          <p className="flex-1">{notice.message}</p>
          <button onClick={() => setNotice(null)} className="text-xs underline">Fermer</button>
        </div>
      )}

      <div className="flex gap-1 border-b border-gray-200 overflow-x-auto">
        {onglets.map((o) => (
          <button
            key={o.value}
            onClick={() => {
              setOnglet(o.value);
              setNotice(null);
            }}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px inline-flex items-center gap-2 whitespace-nowrap ${
              onglet === o.value
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            <o.icone className="w-4 h-4" /> {o.label}
          </button>
        ))}
      </div>

      {onglet === 'visuel' && (
        <OngletVisuel
          evenementId={evenementId}
          imageUrl={evenement.imageUrl}
          onSucces={(m) => succes(m, [])}
          onEchec={echec}
        />
      )}

      {onglet === 'participants' && (
        <OngletParticipants
          evenementId={evenementId}
          referentiel={referentiel}
          onSucces={(m) => succes(m, ['evenement-participants'])}
          onEchec={echec}
        />
      )}

      {onglet === 'repas' && (
        <OngletRepas
          evenementId={evenementId}
          evenement={evenement}
          onSucces={(m) => succes(m, ['evenement-repas'])}
          onEchec={echec}
        />
      )}

      {onglet === 'concert' && (
        <OngletConcert
          evenementId={evenementId}
          evenement={evenement}
          onSucces={(m) => succes(m, ['evenement-concert'])}
          onEchec={echec}
        />
      )}

      {onglet === 'planning' && (
        <OngletPlanning
          evenementId={evenementId}
          referentiel={referentiel}
          dateDebut={evenement.dateDebut}
          dateFin={evenement.dateFin}
          onSucces={(m) => succes(m, ['evenement-planning'])}
          onEchec={echec}
        />
      )}
    </div>
  );
}

/* ================================== Visuel ================================== */

function OngletVisuel({
  evenementId,
  imageUrl,
  onSucces,
  onEchec,
}: {
  evenementId: number;
  imageUrl?: string | null;
  onSucces: (message: string) => void;
  onEchec: (error: Error) => void;
}) {
  const champFichier = useRef<HTMLInputElement>(null);

  const envoyer = useMutation({
    mutationFn: (fichier: File) => envoyerVisuelEvenement(evenementId, fichier),
    onSuccess: () => onSucces('Visuel mis à jour.'),
    onError: onEchec,
  });

  const retirer = useMutation({
    mutationFn: () => retirerVisuelEvenement(evenementId),
    onSuccess: () => onSucces('Visuel retiré.'),
    onError: onEchec,
  });

  return (
    <div className="card lg:p-6 space-y-4">
      {imageUrl ? (
        <img
          src={imageUrl}
          alt="Visuel de l'événement"
          className="w-full max-h-96 object-contain rounded-xl bg-gray-900"
        />
      ) : (
        <div className="w-full h-48 rounded-xl bg-gray-100 flex flex-col items-center justify-center gap-2 text-gray-400">
          <ImageIcon className="w-8 h-8" />
          <p className="text-sm">Aucun visuel pour le moment.</p>
        </div>
      )}

      <p className="text-xs text-gray-500 dark:text-gray-400">
        PNG, JPG ou WEBP, 8 Mo au maximum. L&apos;image est redimensionnée et une vignette est
        générée automatiquement.
      </p>

      <input
        ref={champFichier}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(e) => {
          const fichier = e.target.files?.[0];
          if (fichier) envoyer.mutate(fichier);
          // Réinitialisé pour qu'un même fichier choisi deux fois de suite
          // déclenche bien un second envoi.
          e.target.value = '';
        }}
      />

      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => champFichier.current?.click()}
          disabled={envoyer.isPending}
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
        >
          {envoyer.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Upload className="w-4 h-4" />
          )}
          {imageUrl ? 'Remplacer le visuel' : 'Ajouter un visuel'}
        </button>

        {imageUrl && (
          <button
            onClick={() => {
              if (window.confirm('Retirer le visuel de cet événement ?')) retirer.mutate();
            }}
            disabled={retirer.isPending}
            className="text-red-600 hover:text-red-700 inline-flex items-center gap-1 text-sm px-3"
          >
            <Trash2 className="w-4 h-4" /> Retirer
          </button>
        )}
      </div>
    </div>
  );
}

/* =============================== Participants =============================== */

function OngletParticipants({
  evenementId,
  referentiel,
  onSucces,
  onEchec,
}: {
  evenementId: number;
  referentiel: any;
  onSucces: (message: string) => void;
  onEchec: (error: Error) => void;
}) {
  const [form, setForm] = useState<ParticipantPayload>(participantVide);
  const [enEdition, setEnEdition] = useState<number | null>(null);
  const [ouvert, setOuvert] = useState(false);

  const query = useQuery({
    queryKey: ['evenement-participants', evenementId],
    queryFn: () => fetchParticipants(evenementId),
  });

  const fermer = (message: string) => {
    onSucces(message);
    setOuvert(false);
    setEnEdition(null);
    setForm(participantVide);
  };

  const creer = useMutation({
    mutationFn: () => createParticipant(evenementId, form),
    onSuccess: () => fermer('Participant ajouté.'),
    onError: onEchec,
  });

  const modifier = useMutation({
    mutationFn: () => updateParticipant(evenementId, enEdition as number, form),
    onSuccess: () => fermer('Participant mis à jour.'),
    onError: onEchec,
  });

  const supprimer = useMutation({
    mutationFn: (id: number) => deleteParticipant(evenementId, id),
    onSuccess: () => fermer('Participant retiré.'),
    onError: onEchec,
  });

  if (query.isPending) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
      </div>
    );
  }

  const participants = query.data ?? [];
  const enCours = creer.isPending || modifier.isPending;
  const invalide = enEdition === null && !form.membreId;

  const ouvrirEdition = (p: any) => {
    setForm({
      membreId: p.membre?.id,
      type: p.type ?? 'benevole',
      statut: p.statut ?? 'confirme',
      activitySectorId: p.activitySector?.id ?? null,
      bureauRoleId: p.bureauRole?.id ?? null,
      commentaire: p.commentaire ?? '',
    });
    setEnEdition(p.id);
    setOuvert(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          onClick={() => {
            setForm(participantVide);
            setEnEdition(null);
            setOuvert(!ouvert);
          }}
          className="btn-primary inline-flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Ajouter un participant
        </button>
      </div>

      {ouvert && (
        <div className="card lg:p-5 space-y-3">
          <h2 className="font-semibold">
            {enEdition !== null ? 'Modifier le participant' : 'Nouveau participant'}
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <label className="text-sm text-gray-600 lg:col-span-2 dark:text-gray-400">
              Membre
              <select
                className="input mt-1"
                value={form.membreId ?? ''}
                disabled={enEdition !== null}
                onChange={(e) => setForm({ ...form, membreId: Number(e.target.value) })}
              >
                <option value="">Choisir un membre…</option>
                {(referentiel?.membres ?? []).map((m: any) => (
                  <option key={m.id} value={m.id}>{m.fullName || m.email}</option>
                ))}
              </select>
            </label>

            <label className="text-sm text-gray-600 dark:text-gray-400">
              Type
              <select
                className="input mt-1"
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
              >
                <option value="benevole">Bénévole</option>
                <option value="bureau">Membre du bureau</option>
              </select>
            </label>

            <label className="text-sm text-gray-600 dark:text-gray-400">
              Statut
              <select
                className="input mt-1"
                value={form.statut}
                onChange={(e) => setForm({ ...form, statut: e.target.value })}
              >
                <option value="confirme">Confirmé</option>
                <option value="propose">Proposé</option>
                <option value="refuse">Refusé</option>
              </select>
            </label>

            <label className="text-sm text-gray-600 dark:text-gray-400">
              Poste
              <select
                className="input mt-1"
                value={form.activitySectorId ?? ''}
                onChange={(e) =>
                  setForm({ ...form, activitySectorId: e.target.value ? Number(e.target.value) : null })
                }
              >
                <option value="">Aucun</option>
                {(referentiel?.secteurs ?? []).map((s: any) => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
            </label>

            <label className="text-sm text-gray-600 dark:text-gray-400">
              Fonction au bureau
              <select
                className="input mt-1"
                value={form.bureauRoleId ?? ''}
                onChange={(e) =>
                  setForm({ ...form, bureauRoleId: e.target.value ? Number(e.target.value) : null })
                }
              >
                <option value="">Aucune</option>
                {(referentiel?.rolesBureau ?? []).map((r: any) => (
                  <option key={r.id} value={r.id}>{r.label}</option>
                ))}
              </select>
            </label>

            <textarea
              className="input lg:col-span-2 min-h-[4rem]"
              placeholder="Commentaire"
              value={form.commentaire ?? ''}
              onChange={(e) => setForm({ ...form, commentaire: e.target.value })}
            />
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => (enEdition !== null ? modifier.mutate() : creer.mutate())}
              disabled={enCours || invalide}
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
            >
              {enCours && <Loader2 className="w-4 h-4 animate-spin" />}
              {enEdition !== null ? 'Enregistrer' : 'Ajouter'}
            </button>
            <button onClick={() => setOuvert(false)} className="text-sm text-gray-500 dark:text-gray-400">Annuler</button>
          </div>
        </div>
      )}

      {participants.length === 0 ? (
        <p className="text-gray-500 text-sm dark:text-gray-400">Aucun participant pour le moment.</p>
      ) : (
        <div className="space-y-2">
          {participants.map((p: any) => (
            <div
              key={p.id}
              className="card p-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3"
            >
              <div className="text-sm space-y-1">
                <p className="font-semibold text-gray-900 flex flex-wrap items-center gap-2 dark:text-gray-100">
                  {p.membre?.fullName ?? p.membre?.email ?? 'Membre supprimé'}
                  <span className={`text-xs px-2 py-0.5 rounded-full ${couleurStatut[p.statut] ?? 'bg-gray-100'}`}>
                    {p.statut}
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 dark:text-gray-300 dark:bg-gray-800">
                    {p.type === 'bureau' ? 'Bureau' : 'Bénévole'}
                  </span>
                </p>
                <p className="text-gray-600 dark:text-gray-400">
                  {p.activitySector?.label ?? 'Sans poste'}
                  {p.bureauRole ? ` — ${p.bureauRole.label}` : ''}
                  {p.nbCreneaux > 0 ? ` — ${p.nbCreneaux} créneau(x)` : ''}
                </p>
                {p.commentaire && <p className="text-gray-500 italic dark:text-gray-400">{p.commentaire}</p>}
              </div>

              <div className="flex items-center gap-4 shrink-0">
                <button
                  onClick={() => ouvrirEdition(p)}
                  className="text-primary-700 hover:text-primary-800 inline-flex items-center gap-1 text-sm"
                >
                  <Pencil className="w-4 h-4" /> Modifier
                </button>
                <button
                  onClick={() => {
                    if (window.confirm('Retirer ce participant ?')) supprimer.mutate(p.id);
                  }}
                  disabled={supprimer.isPending}
                  className="text-red-600 hover:text-red-700 inline-flex items-center gap-1 text-sm"
                >
                  <Trash2 className="w-4 h-4" /> Retirer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ================================= Planning ================================= */

function OngletPlanning({
  evenementId,
  referentiel,
  dateDebut,
  dateFin,
  onSucces,
  onEchec,
}: {
  evenementId: number;
  referentiel: any;
  dateDebut?: string | null;
  dateFin?: string | null;
  onSucces: (message: string) => void;
  onEchec: (error: Error) => void;
}) {
  const [form, setForm] = useState<CreneauPayload>(creneauVide);
  const [enEdition, setEnEdition] = useState<number | null>(null);
  const [ouvert, setOuvert] = useState(false);
  // La grille est la vue de travail ; la liste garde ce qu'elle ne sait pas
  // exprimer : un créneau à cheval sur deux heures, un commentaire.
  const [vue, setVue] = useState<'grille' | 'liste'>('grille');

  const query = useQuery({
    queryKey: ['evenement-planning', evenementId],
    queryFn: () => fetchPlanningEvenement(evenementId),
  });

  const participantsQuery = useQuery({
    queryKey: ['evenement-participants', evenementId],
    queryFn: () => fetchParticipants(evenementId),
  });

  const fermer = (message: string) => {
    onSucces(message);
    setOuvert(false);
    setEnEdition(null);
    setForm(creneauVide);
  };

  /**
   * Le conflit d'horaire opposé par le serveur, et le geste à rejouer.
   *
   * Même arbitrage que dans la grille : on refuse d'affecter quelqu'un à deux
   * endroits à la fois, mais on laisse passer outre — après l'avoir montré.
   */
  const [conflit, setConflit] = useState<{ message: string; forcer: () => void } | null>(null);

  const arbitrer = (erreur: Error, forcer: () => void) => {
    const donnees = (erreur as Error & { donnees?: { conflit?: boolean } }).donnees;
    if (donnees?.conflit) {
      setConflit({ message: erreur.message, forcer });
      return;
    }
    onEchec(erreur);
  };

  const creer = useMutation({
    mutationFn: (forcer?: boolean) => createCreneau(evenementId, { ...form, forcer }),
    onSuccess: () => {
      setConflit(null);
      fermer('Créneau ajouté.');
    },
    onError: (erreur: Error) => arbitrer(erreur, () => creer.mutate(true)),
  });

  const modifier = useMutation({
    mutationFn: (forcer?: boolean) =>
      updateCreneau(evenementId, enEdition as number, { ...form, forcer }),
    onSuccess: () => {
      setConflit(null);
      fermer('Créneau mis à jour.');
    },
    onError: (erreur: Error) => arbitrer(erreur, () => modifier.mutate(true)),
  });

  const supprimer = useMutation({
    mutationFn: (id: number) => deleteCreneau(evenementId, id),
    onSuccess: () => fermer('Créneau supprimé.'),
    onError: onEchec,
  });

  if (query.isPending) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
      </div>
    );
  }

  const creneaux = query.data ?? [];
  const enCours = creer.isPending || modifier.isPending;
  const invalide = !form.benevoleId || !form.debut || !form.fin;
  const totalHeures = creneaux.reduce((somme: number, c: any) => somme + (c.nbHeures ?? 0), 0);

  const ouvrirEdition = (c: any) => {
    setForm({
      benevoleId: c.benevole?.id,
      eventParticipantId: c.eventParticipant?.id ?? null,
      mission: c.mission ?? 'accueil',
      statutConfirmation: c.statutConfirmation ?? 'propose',
      debut: pourChamp(c.debut),
      fin: pourChamp(c.fin),
      commentaire: c.commentaire ?? '',
    });
    setEnEdition(c.id);
    setOuvert(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {creneaux.length} créneau(x) — {totalHeures} h au total
        </p>
        <div className="flex items-center gap-2">
          {/* La grille est la vue de travail ; la liste garde la saisie fine. */}
          <div className="inline-flex rounded-xl bg-gray-100 p-0.5">
            {([
              { valeur: 'grille' as const, label: 'Grille' },
              { valeur: 'liste' as const, label: 'Liste' },
            ]).map((v) => (
              <button
                key={v.valeur}
                type="button"
                onClick={() => setVue(v.valeur)}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  vue === v.valeur ? 'bg-white shadow-sm font-medium' : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                {v.label}
              </button>
            ))}
          </div>

          {/* La feuille du soir : le même planning, mais posé à plat pour un
              pouce et un téléphone. On y va depuis ici, la veille au soir. */}
          <Link
            to={`/bureau/evenements/${evenementId}/jour-j`}
            className="btn-outline inline-flex items-center gap-2 text-sm"
          >
            <ClipboardCheck className="w-4 h-4" /> Jour J
          </Link>

          <button
            onClick={() => {
              setForm(creneauVide);
              setEnEdition(null);
              setOuvert(!ouvert);
            }}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Ajouter un créneau
          </button>
        </div>
      </div>

      {ouvert && (
        <div className="card lg:p-5 space-y-3">
          <h2 className="font-semibold">
            {enEdition !== null ? 'Modifier le créneau' : 'Nouveau créneau'}
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <label className="text-sm text-gray-600 dark:text-gray-400">
              Bénévole
              <select
                className="input mt-1"
                value={form.benevoleId ?? ''}
                onChange={(e) => setForm({ ...form, benevoleId: Number(e.target.value) })}
              >
                <option value="">Choisir un bénévole…</option>
                {(referentiel?.benevoles ?? []).map((b: any) => (
                  <option key={b.id} value={b.id}>{b.fullName}</option>
                ))}
              </select>
            </label>

            <label className="text-sm text-gray-600 dark:text-gray-400">
              Participant rattaché (facultatif)
              <select
                className="input mt-1"
                value={form.eventParticipantId ?? ''}
                onChange={(e) =>
                  setForm({
                    ...form,
                    eventParticipantId: e.target.value ? Number(e.target.value) : null,
                  })
                }
              >
                <option value="">Aucun</option>
                {(participantsQuery.data ?? []).map((p: any) => (
                  <option key={p.id} value={p.id}>{p.membre?.fullName ?? p.membre?.email}</option>
                ))}
              </select>
            </label>

            <label className="text-sm text-gray-600 dark:text-gray-400">
              Mission
              <select
                className="input mt-1"
                value={form.mission}
                onChange={(e) => setForm({ ...form, mission: e.target.value })}
              >
                {Object.entries(referentiel?.missions ?? {}).map(([valeur, libelle]) => (
                  <option key={valeur} value={valeur}>{libelle as string}</option>
                ))}
              </select>
            </label>

            <label className="text-sm text-gray-600 dark:text-gray-400">
              Confirmation
              <select
                className="input mt-1"
                value={form.statutConfirmation}
                onChange={(e) => setForm({ ...form, statutConfirmation: e.target.value })}
              >
                {Object.entries(referentiel?.statutsCreneau ?? {}).map(([valeur, libelle]) => (
                  <option key={valeur} value={valeur}>{libelle as string}</option>
                ))}
              </select>
            </label>

            <label className="text-sm text-gray-600 dark:text-gray-400">
              Début
              <input
                className="input mt-1"
                type="datetime-local"
                value={form.debut}
                onChange={(e) => setForm({ ...form, debut: e.target.value })}
              />
            </label>

            <label className="text-sm text-gray-600 dark:text-gray-400">
              Fin
              <input
                className="input mt-1"
                type="datetime-local"
                value={form.fin}
                onChange={(e) => setForm({ ...form, fin: e.target.value })}
              />
            </label>

            <textarea
              className="input lg:col-span-2 min-h-[4rem]"
              placeholder="Commentaire"
              value={form.commentaire ?? ''}
              onChange={(e) => setForm({ ...form, commentaire: e.target.value })}
            />
          </div>

          {/* Le conflit s'affiche sous le formulaire, là où l'on vient de
              saisir : la personne est déjà ailleurs à cette heure-là. */}
          {conflit && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 p-3 flex flex-wrap items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
              <p className="text-sm text-amber-900 dark:text-amber-200 flex-1 min-w-[12rem]">
                {conflit.message} Personne ne peut être à deux endroits à la fois — sauf si vous
                savez pourquoi.
              </p>
              <button
                type="button"
                onClick={() => conflit.forcer()}
                className="text-sm px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-medium"
              >
                Affecter quand même
              </button>
            </div>
          )}

          <div className="flex gap-2">
            <button
              onClick={() => (enEdition !== null ? modifier.mutate(undefined) : creer.mutate(undefined))}
              disabled={enCours || invalide}
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
            >
              {enCours && <Loader2 className="w-4 h-4 animate-spin" />}
              {enEdition !== null ? 'Enregistrer' : 'Ajouter'}
            </button>
            <button onClick={() => setOuvert(false)} className="text-sm text-gray-500 dark:text-gray-400">Annuler</button>
          </div>
        </div>
      )}

      {vue === 'grille' && (
        <GrillePlanning
          evenementId={evenementId}
          creneaux={creneaux}
          referentiel={referentiel}
          dateDebut={dateDebut}
          dateFin={dateFin}
          onSucces={onSucces}
          onEchec={onEchec}
        />
      )}
      {vue === 'liste' && (creneaux.length === 0 ? (
        <p className="text-gray-500 text-sm dark:text-gray-400">Aucun créneau pour le moment.</p>
      ) : (
        <div className="space-y-2">
          {creneaux.map((c: any) => (
            <div
              key={c.id}
              className="card p-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3"
            >
              <div className="text-sm space-y-1">
                <p className="font-semibold text-gray-900 flex flex-wrap items-center gap-2 dark:text-gray-100">
                  {c.benevole?.fullName ?? 'Bénévole supprimé'}
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      couleurStatut[c.statutConfirmation] ?? 'bg-gray-100'
                    }`}
                  >
                    {c.statutConfirmation}
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-primary-50 text-primary-700">
                    {c.missionLabel ?? c.mission}
                  </span>
                </p>
                <p className="text-gray-600 dark:text-gray-400">
                  {c.debut && format(new Date(c.debut), 'EEE d MMM HH:mm', { locale: fr })}
                  {c.fin && ` → ${format(new Date(c.fin), 'HH:mm')}`}
                  {c.nbHeures ? ` (${c.nbHeures} h)` : ''}
                </p>
                {c.commentaire && <p className="text-gray-500 italic dark:text-gray-400">{c.commentaire}</p>}
              </div>

              <div className="flex items-center gap-4 shrink-0">
                <button
                  onClick={() => ouvrirEdition(c)}
                  className="text-primary-700 hover:text-primary-800 inline-flex items-center gap-1 text-sm"
                >
                  <Pencil className="w-4 h-4" /> Modifier
                </button>
                <button
                  onClick={() => {
                    if (window.confirm('Supprimer ce créneau ?')) supprimer.mutate(c.id);
                  }}
                  disabled={supprimer.isPending}
                  className="text-red-600 hover:text-red-700 inline-flex items-center gap-1 text-sm"
                >
                  <Trash2 className="w-4 h-4" /> Supprimer
                </button>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
